import React, { useEffect, useRef, useState } from "react";
import { PolotnoContainer } from "polotno";
import { Workspace } from "polotno/canvas/workspace";
import { createStore } from "polotno/model/store";

/**
 * PolotnoPreview - Renders a Polotno template as a non-interactive preview
 * Uses actual Polotno Workspace to render, then exports as PNG
 *
 * This avoids SVG data URL size limits by:
 * 1. Mounting a <Workspace /> component (required for export)
 * 2. Loading template JSON with dynamic content
 * 3. Exporting as PNG blob
 * 4. Creating blob URL for display
 */
const PolotnoPreview = ({ templateJSON, onPreviewReady, className = "" }) => {
  const [store] = useState(() =>
    createStore({
      key: process.env.REACT_APP_POLOTNO_LICENSE_KEY_DEV,
      showCredit: false
    })
  );

  const [previewUrl, setPreviewUrl] = useState(null);
  const workspaceRef = useRef(null);
  const hasGeneratedRef = useRef(false);
  const onPreviewReadyRef = useRef(onPreviewReady);

  // Keep the callback ref updated without triggering re-renders
  useEffect(() => {
    onPreviewReadyRef.current = onPreviewReady;
  }, [onPreviewReady]);

  useEffect(() => {
    if (!templateJSON || hasGeneratedRef.current) return;

    let currentUrl = null;

    const generatePreview = async () => {
      try {
        // Load template JSON into store
        store.loadJSON(templateJSON);

        // Wait for workspace to mount and images to load
        // Increased timeout to 2000ms to allow CloudFront images (especially logos) to load
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Check if all images have loaded
        const page = store.pages[0];
        if (page) {
          const imageElements = page.children.filter(
            (child) => child.type === "image"
          );
          console.log(`📸 Template has ${imageElements.length} image elements`);

          // Wait a bit more if there are images
          if (imageElements.length > 0) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }

        // Export as PNG blob (0.3 pixelRatio = 324px for 1080px template)
        const blob = await store.toBlob({
          pageId: store.pages[0]?.id,
          pixelRatio: 0.3
        });

        // Create blob URL
        const url = URL.createObjectURL(blob);
        currentUrl = url;
        setPreviewUrl(url);

        // Mark as generated
        hasGeneratedRef.current = true;

        // Notify parent component using ref (stable reference)
        if (onPreviewReadyRef.current) {
          onPreviewReadyRef.current(url);
        }

        console.log("✅ Generated preview:", {
          size: `${(blob.size / 1024).toFixed(1)}KB`,
          url: url.substring(0, 60)
        });
      } catch (error) {
        console.error("❌ Failed to generate preview:", error);
        // Still notify parent even on error so rendering can continue
        hasGeneratedRef.current = true;
        if (onPreviewReadyRef.current) {
          onPreviewReadyRef.current(null);
        }
      }
    };

    generatePreview();

    // Cleanup blob URL on unmount
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  return (
    <div className={`relative ${className}`}>
      {/* Hidden workspace for rendering */}
      <div
        ref={workspaceRef}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1
        }}
      >
        <PolotnoContainer className="size-full">
          <Workspace store={store} />
        </PolotnoContainer>
      </div>

      {/* Preview image */}
      {previewUrl ? (
        <img
          src={previewUrl}
          alt="Template Preview"
          className="size-full object-cover"
          draggable={false}
          style={{ pointerEvents: "none" }}
        />
      ) : (
        <div className="flex size-full items-center justify-center">
          <div style={{ color: "#9CA3AF" }}>Generating preview...</div>
        </div>
      )}
    </div>
  );
};

export default PolotnoPreview;
